/**
 * 荷姿換算に関するユーティリティ関数
 * 明細の数量はバラ総数で扱い、荷姿(ケース/ボール等)は換算表示にのみ使う。
 *
 * 数量も入数も小数を取りうるため、換算の割り算・引き算は Decimal で行う。
 * 浮動小数点のままだと Math.floor(3.3 / 1.1) が 2 になる(期待は3)ように商が1つずれる。
 */
import { Decimal } from 'decimal.js';

/** 荷姿(名前と入数) */
export interface PackagingInfo {
  name: string;
  /** 入数(バラ何個分か) */
  factor: number;
}

/**
 * 入数が荷姿換算の対象になるか判定する。
 *
 * 0以下は荷姿を持たない(未設定)。1は「1荷姿=バラ1」でケース数が数量と同義になり
 * 情報量が無いため対象外にする。入数は小数を取りうるので、0.5 のような1未満の値は
 * 荷姿として有効に扱う(数量100・入数0.5なら200ケース)。
 */
const hasPackaging = (snp: number | null | undefined): snp is number => snp != null && snp > 0 && snp !== 1;

/**
 * 商品バリエーションの荷姿名・入数から荷姿リストを組み立てる。
 * 荷姿名か入数が欠けている場合は換算表示を出さないため空配列を返す。
 */
export const buildPackagings = (
  packagingTypeName: string | null | undefined,
  snp: number | null | undefined,
): PackagingInfo[] => {
  if (!packagingTypeName || !hasPackaging(snp)) {
    return [];
  }
  return [{ name: packagingTypeName, factor: snp }];
};

/**
 * バラ総数の荷姿換算表示を組み立てる: 「= 2ケース + 4個」
 * 最大荷姿は0でも表示し(荷姿あり商品であることを示す)、後続の荷姿の0は省略する。
 * 単位が未設定(unitLabel が空)の商品はバラ数のみを「= 2ケース + 4」と表示する。
 */
export const formatPackagingBreakdown = (
  quantity: number,
  packagings: PackagingInfo[],
  unitLabel: string | null,
): string | null => {
  if (quantity <= 0 || packagings.length === 0) {
    return null;
  }
  const sorted = [...packagings].filter((p) => hasPackaging(p.factor)).sort((a, b) => b.factor - a.factor);
  if (sorted.length === 0) {
    return null;
  }
  let rest = new Decimal(quantity);
  const parts: string[] = [];
  sorted.forEach((packaging, index) => {
    const count = rest.div(packaging.factor).floor();
    rest = rest.minus(count.mul(packaging.factor));
    if (index === 0 || count.greaterThan(0)) {
      parts.push(`${count.toNumber().toLocaleString()}${packaging.name}`);
    }
  });
  if (rest.greaterThan(0)) {
    parts.push(`${rest.toNumber().toLocaleString()}${unitLabel ?? ''}`);
  }
  return `= ${parts.join(' + ')}`;
};
